import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import data from './bodoBordeaux.json';
import RatingLookup from './RatingLookup';
import './BordeauxDeals.css';

const kr = value =>
  value == null ? '—' : `${Math.round(value).toLocaleString('nb-NO')} kr`;

// Saving as a share of what the wine would otherwise cost you.
const savingPct = wine =>
  wine.savingVsElsewhereNOK && wine.cheapestElsewhereNOK
    ? (wine.savingVsElsewhereNOK / wine.cheapestElsewhereNOK) * 100
    : 0;

// Unknown scarcity sorts last rather than pretending to be rare.
const storeCount = wine =>
  wine.otherStoreCount == null ? Number.MAX_SAFE_INTEGER : wine.otherStoreCount;

const topScore = wine =>
  wine.ratings && wine.ratings.length ? wine.ratings[0].score : null;

function tierClass(tier) {
  return `bd-tier bd-tier-${tier}`;
}

// The points are the headline, so they sit next to the price rather than
// buried in the expanded body.
function ScoreBadge({ wine }) {
  const score = topScore(wine);
  if (score == null) return null;
  const band = score >= 95 ? 'top' : score >= 90 ? 'high' : score >= 85 ? 'mid' : 'low';
  return <span className={`bd-score bd-score-${band}`}>{score}</span>;
}

function RatingBlock({ wine, t }) {
  if (!wine.ratings || wine.ratings.length === 0) return null;
  return (
    <div className="bd-ratings">
      {wine.ratings.map(rating => (
        <div className="bd-rating" key={rating.url}>
          <span className="bd-rating-score">
            {rating.score}
            <small>{`/${rating.best}`}</small>
          </span>
          <div className="bd-rating-text">
            {rating.note && <p className="bd-rating-note">{rating.note}</p>}
            <span className="bd-rating-src">
              {rating.tasted
                ? t(
                    'grapeEscape.bordeaux.ratedByOn',
                    '{{source}}, tasted {{date}}',
                    { source: rating.source, date: rating.tasted }
                  )
                : t('grapeEscape.bordeaux.ratedBy', 'Rated by {{source}}', {
                    source: rating.source,
                  })}
              {' · '}
              <a href={rating.url} target="_blank" rel="noreferrer">
                {t('grapeEscape.bordeaux.readReview', 'read it at the source')}
              </a>
            </span>
          </div>
        </div>
      ))}
    </div>
  );
}

function RareBadge({ wine, t }) {
  if (!wine.scarce) return null;
  const count = wine.otherStoreCount;
  return (
    <span className="bd-badge bd-badge-rare">
      {count === 0
        ? t('grapeEscape.bordeaux.onlyHere', 'Only here')
        : t('grapeEscape.bordeaux.rareBadge', 'Rare · {{count}} other shops', {
            count,
          })}
    </span>
  );
}

function DealBadge({ wine, t }) {
  const saving = wine.savingVsElsewhereNOK;
  if (saving == null || saving < 1) return null;
  const pct = savingPct(wine);
  return (
    <span className="bd-badge bd-badge-save">
      {t('grapeEscape.bordeaux.saveBadge', 'Save {{amount}} kr', {
        amount: Math.round(saving),
      })}
      <span className="bd-badge-pct">{`${pct.toFixed(1).replace('.', ',')} %`}</span>
    </span>
  );
}

function WineRow({ wine, t }) {
  const [open, setOpen] = useState(false);
  const alternatives = wine.otherListings || [];

  return (
    <li className="bd-wine">
      <button
        type="button"
        className="bd-wine-head"
        onClick={() => setOpen(o => !o)}
        aria-expanded={open}
      >
        <span className="bd-wine-main">
          <span className="bd-wine-name">{wine.name}</span>
          <span className="bd-wine-meta">
            <span className={tierClass(wine.tier)}>{wine.appellation}</span>
            {wine.cellarRating && <span className="bd-chip">{wine.cellarRating}</span>}
            {wine.stockAtStore && <span className="bd-chip">{wine.stockAtStore}</span>}
          </span>
        </span>
        <span className="bd-wine-right">
          <ScoreBadge wine={wine} />
          <span className="bd-price">{kr(wine.priceNOK)}</span>
          <RareBadge wine={wine} t={t} />
          <DealBadge wine={wine} t={t} />
        </span>
      </button>

      {open && (
        <div className="bd-wine-body">
          <RatingBlock wine={wine} t={t} />

          <div className="bd-stats">
            <div>
              <span className="bd-stat-label">
                {t('grapeEscape.bordeaux.vsAppellation', 'Versus {{appellation}}', {
                  appellation: wine.appellation,
                })}
              </span>
              <span className="bd-stat-value">
                {wine.peerMedianNOK == null
                  ? t('grapeEscape.bordeaux.tooFewPeers', 'Too few comparable wines')
                  : t(
                      'grapeEscape.bordeaux.medianLine',
                      'Median {{median}} kr across {{count}} wines, this one is {{delta}}%',
                      {
                        median: Math.round(wine.peerMedianNOK),
                        count: wine.peerCount,
                        delta: wine.vsMedianPct > 0
                          ? `+${Math.round(wine.vsMedianPct)}`
                          : Math.round(wine.vsMedianPct),
                      }
                    )}
              </span>
            </div>
            <div>
              <span className="bd-stat-label">
                {t('grapeEscape.bordeaux.quality', 'Quality signal')}
              </span>
              <span className="bd-stat-value">
                {wine.tierLabel}
                {wine.vintage ? ` · ${wine.vintage}` : ''}
                {wine.abv ? ` · ${wine.abv}%` : ''}
              </span>
            </div>
          </div>

          {wine.cheapestElsewhereNOK && wine.savingVsElsewhereNOK >= 1 && (
            <p className="bd-saving-line">
              {t(
                'grapeEscape.bordeaux.savingLine',
                'Cheapest elsewhere is {{elsewhere}} kr, so you save {{amount}} kr ({{pct}} %).',
                {
                  elsewhere: Math.round(wine.cheapestElsewhereNOK),
                  amount: Math.round(wine.savingVsElsewhereNOK),
                  pct: savingPct(wine).toFixed(1).replace('.', ','),
                }
              )}
            </p>
          )}

          {wine.otherStoreCount != null && (
            <p className="bd-scarcity-line">
              {wine.otherStoreCount === 0
                ? t(
                    'grapeEscape.bordeaux.scarcityNone',
                    'No other Vinmonopolet shop has this.'
                  )
                : t(
                    'grapeEscape.bordeaux.scarcityLine',
                    'Also on the shelf at {{count}} other shops: {{stores}}.',
                    {
                      count: wine.otherStoreCount,
                      stores: wine.otherStores.join(', '),
                    }
                  )}{' '}
              {wine.orderable
                ? t('grapeEscape.bordeaux.canOrder', 'It can also be ordered online.')
                : t(
                    'grapeEscape.bordeaux.cannotOrder',
                    'It cannot be ordered online, so you have to go to a shop that has it.'
                  )}
              {wine.listingStoreCount != null &&
                alternatives.length > 0 &&
                wine.listingStoreCount <= wine.otherStoreCount && (
                  <span className="bd-scarcity-split">
                    {t(
                      'grapeEscape.bordeaux.scarcitySplit',
                      ' Vinmonopolet shows this varenummer in only {{own}} shops, which makes it look rarer than it is. The same wine sits under {{codes}} more varenumre elsewhere.',
                      {
                        own: wine.listingStoreCount,
                        codes: alternatives.length,
                      }
                    )}
                  </span>
                )}
            </p>
          )}

          {alternatives.length > 0 && (
            <div className="bd-alts">
              <span className="bd-stat-label">
                {t(
                  'grapeEscape.bordeaux.otherListings',
                  'Same wine under other product codes'
                )}
              </span>
              <ul>
                {alternatives.map(alt => (
                  <li key={alt.code} className={alt.obtainable ? '' : 'bd-alt-dead'}>
                    <span className="bd-alt-price">{kr(alt.priceNOK)}</span>
                    <span className="bd-alt-code">{alt.code}</span>
                    <span className="bd-alt-avail">
                      {alt.obtainable
                        ? alt.availability
                        : t('grapeEscape.bordeaux.soldOut', 'Sold out')}
                    </span>
                    {alt.storeCount != null && (
                      <span className="bd-alt-shops">
                        {t('grapeEscape.bordeaux.altShops', '{{count}} shops', {
                          count: alt.storeCount,
                        })}
                      </span>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          )}

          <a className="bd-link" href={wine.url} target="_blank" rel="noreferrer">
            {t('grapeEscape.bordeaux.openAtVmp', 'Open at Vinmonopolet')}
          </a>

          <RatingLookup name={wine.name} bordeaux />
        </div>
      )}
    </li>
  );
}

function BordeauxDeals() {
  const { t } = useTranslation();
  const [onlyDeals, setOnlyDeals] = useState(false);
  const [onlyRare, setOnlyRare] = useState(false);
  const [minPrice, setMinPrice] = useState('');
  const [maxPrice, setMaxPrice] = useState('');
  const [sort, setSort] = useState('saving');

  const wines = useMemo(() => {
    let list = data.wines;
    if (onlyDeals) list = list.filter(w => (w.savingVsElsewhereNOK || 0) >= 1);
    if (onlyRare) list = list.filter(w => w.scarce);
    const floor = Number(minPrice);
    if (floor > 0) list = list.filter(w => w.priceNOK >= floor);
    const cap = Number(maxPrice);
    if (cap > 0) list = list.filter(w => w.priceNOK <= cap);
    const sorters = {
      saving: (a, b) => (b.savingVsElsewhereNOK || 0) - (a.savingVsElsewhereNOK || 0),
      savingPct: (a, b) => savingPct(b) - savingPct(a),
      rare: (a, b) => storeCount(a) - storeCount(b) || b.priceNOK - a.priceNOK,
      score: (a, b) => (topScore(b) ?? -1) - (topScore(a) ?? -1),
      deal: (a, b) => b.dealScore - a.dealScore,
      priceUp: (a, b) => a.priceNOK - b.priceNOK,
      priceDown: (a, b) => b.priceNOK - a.priceNOK,
    };
    return [...list].sort(sorters[sort]);
  }, [onlyDeals, onlyRare, minPrice, maxPrice, sort]);

  const totalSaving = data.wines.reduce(
    (sum, w) => sum + Math.max(w.savingVsElsewhereNOK || 0, 0),
    0
  );
  const dealCount = data.wines.filter(w => (w.savingVsElsewhereNOK || 0) >= 1).length;
  const rareCount = data.wines.filter(w => w.scarce).length;
  const bestPct = data.wines.reduce((best, w) => Math.max(best, savingPct(w)), 0);

  return (
    <div className="bordeaux-deals">
      <header className="bd-header">
        <h2>
          {t('grapeEscape.bordeaux.title', 'Bordeaux at {{store}}', {
            store: data.store.name,
          })}
        </h2>
        <p className="bd-sub">
          {t(
            'grapeEscape.bordeaux.subtitle',
            '{{total}} bottles over {{min}} kr on the shelf. {{deals}} of them are the cheapest listing of that wine anywhere in Norway.',
            {
              total: data.wines.length,
              min: Math.round(data.minPriceNOK),
              deals: dealCount,
            }
          )}
        </p>
        <p className="bd-explainer">
          {t(
            'grapeEscape.bordeaux.explainer',
            'Vinmonopolet lists the same wine under one product code per import batch, each with its own price, and a store stocks a particular code. That is why the identical bottle can be cheaper here than elsewhere.'
          )}
        </p>
      </header>

      <div className="bd-summary">
        <div>
          <strong>{dealCount}</strong>
          <span>{t('grapeEscape.bordeaux.cheapestHere', 'cheapest here')}</span>
        </div>
        <div>
          <strong>{rareCount}</strong>
          <span>{t('grapeEscape.bordeaux.rareCount', 'hard to find elsewhere')}</span>
        </div>
        <div>
          <strong>{kr(totalSaving)}</strong>
          <span>{t('grapeEscape.bordeaux.totalSaving', 'total saving')}</span>
        </div>
        <div>
          <strong>{`${bestPct.toFixed(1).replace('.', ',')} %`}</strong>
          <span>{t('grapeEscape.bordeaux.bestPct', 'best discount')}</span>
        </div>
        <div>
          <strong>{data.generatedAt}</strong>
          <span>{t('grapeEscape.bordeaux.checked', 'prices checked')}</span>
        </div>
      </div>

      <div className="bd-controls">
        <label className="bd-toggle">
          <input
            type="checkbox"
            checked={onlyRare}
            onChange={e => setOnlyRare(e.target.checked)}
          />
          {t(
            'grapeEscape.bordeaux.onlyRare',
            'Only wines you can hardly get anywhere else'
          )}
        </label>
        <label className="bd-toggle">
          <input
            type="checkbox"
            checked={onlyDeals}
            onChange={e => setOnlyDeals(e.target.checked)}
          />
          {t('grapeEscape.bordeaux.onlyDeals', 'Only wines that are cheaper here')}
        </label>
        <label>
          {t('grapeEscape.bordeaux.minPrice', 'Min price')}
          <input
            type="number"
            min="300"
            step="50"
            value={minPrice}
            placeholder="300"
            onChange={e => setMinPrice(e.target.value)}
          />
        </label>
        <label>
          {t('grapeEscape.bordeaux.maxPrice', 'Max price')}
          <input
            type="number"
            min="300"
            step="50"
            value={maxPrice}
            placeholder="—"
            onChange={e => setMaxPrice(e.target.value)}
          />
        </label>
        <label>
          {t('grapeEscape.bordeaux.sortBy', 'Sort by')}
          <select value={sort} onChange={e => setSort(e.target.value)}>
            <option value="saving">{t('grapeEscape.bordeaux.sortSaving', 'Saving, kr')}</option>
            <option value="savingPct">
              {t('grapeEscape.bordeaux.sortSavingPct', 'Saving, %')}
            </option>
            <option value="rare">
              {t('grapeEscape.bordeaux.sortRare', 'Rarity, fewest shops first')}
            </option>
            <option value="score">
              {t('grapeEscape.bordeaux.sortScore', 'Points, best first')}
            </option>
            <option value="deal">{t('grapeEscape.bordeaux.sortDeal', 'Deal score')}</option>
            <option value="priceUp">
              {t('grapeEscape.bordeaux.sortPriceUp', 'Price, low to high')}
            </option>
            <option value="priceDown">
              {t('grapeEscape.bordeaux.sortPriceDown', 'Price, high to low')}
            </option>
          </select>
        </label>
      </div>

      <p className="bd-count">
        {t('grapeEscape.bordeaux.showing', 'Showing {{count}} of {{total}} wines', {
          count: wines.length,
          total: data.wines.length,
        })}
      </p>

      {wines.length === 0 ? (
        <p className="bd-empty">
          {t('grapeEscape.bordeaux.empty', 'No wines match those filters.')}
        </p>
      ) : (
        <ul className="bd-list">
          {wines.map(wine => (
            <WineRow key={wine.code} wine={wine} t={t} />
          ))}
        </ul>
      )}
    </div>
  );
}

export default BordeauxDeals;
